import CircleIcon from '@mui/icons-material/Circle';
import { Box, FormControl, MenuItem, Select, Stack, Typography } from "@mui/material";
import makeStyles from '@mui/styles/makeStyles';
import { produce } from "immer";
import { FC, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { DashboardsContext } from "../../../contexts/DashboardsContext";
import { GroupSettingsContext } from "../../../contexts/GroupSettingsContext";
import useBulkDashboards from "../../../hooks/useBulkDashboards";
import { updateDashboardFunc } from "../../../lib/helper";
import theme from "../../../theme";
import { ColorOptions } from "../../../theme/colors";
import { collectionKey } from '../../organisms/dashboards/DashboardsView';

const useStyles = makeStyles((theme) => ({
    expandMoreIcon: {
        color: theme.colors.neutral['500'],
        width: '20px',
        height: '20px',
        transition: '0.25s ease-in-out ',
        '&:hover': {
            color: theme.colors.neutral['700'],
        },
    },
    expandButton: {
        minWidth: '20px',
        padding: 0,
        '&:hover': {
            backgroundColor: 'transparent'
        }
    },
    formContainer: {
        minWidth: '105px',
        padding: 0,
        border: 0,
        "& > .MuiInputBase-root":{
            height: '30px',
            width: 'auto',
            borderRadius: '24px',
            "& > .MuiSvgIcon-root": {
                width: 28,
                height: 28,
            },
            "& fieldset": {
                border: 0,
            },
        },
    },
    menuItem: {
        width: '100%',
    },
    badge: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        height: '30px',
        width: 'auto',
        padding: '0 4px',
        borderRadius: 24,
        fontFamily: 'Inter',
        fontSize: '0.8rem',
        fontWeight: '500',
        textTransform: 'capitalize',
    },
}));

const CompaniesStatusMenu: FC<{}> = () => {
    const classes = useStyles();
    const { getBulkDashboards, clearBulk } = useBulkDashboards();
    const { setDashboards } = useContext(DashboardsContext);
    const { statuses } = useContext(GroupSettingsContext);
    const [status, setSelected] = useState<string>('');

    const bulkDashboards = useMemo(() => getBulkDashboards(collectionKey), [getBulkDashboards]);

    const palettes = useMemo(() => Array.from(new Set(Object.values(ColorOptions)
        .filter(color => !['neutral', 'brown', 'lime', 'pink', 'red', 'shades'].includes(color)))), []);

    const getColors = useCallback((title: string, withBorder?: boolean) => {
        const colorMap: Record<string, { bg: string; text: string; border: string }> = {
            'New':   { bg: '#F0FDFA', text: '#0D9488', border: '#99F6E4' }, // teal-50 / teal-600 / teal-200
            'Lead':  { bg: '#EFF6FF', text: '#2563EB', border: '#BFDBFE' }, // blue-50 / blue-600 / blue-200
            'Tier 1': { bg: '#FFF7ED', text: '#EA580C', border: '#FED7AA' }, // orange-50 / orange-600 / orange-200
            'Tier 2': { bg: '#FFFBEB', text: '#D97706', border: '#FDE68A' }, // amber-50 / amber-600 / amber-200
            'Tier 3': { bg: '#FEFCE8', text: '#CA8A04', border: '#FEF08A' }, // yellow-50 / yellow-600 / yellow-200
            'Won':   { bg: '#F0FDF4', text: '#16A34A', border: '#BBF7D0' }, // green-50 / green-600 / green-200
            'Lost':  { bg: '#F9FAFB', text: '#4B5563', border: '#E5E7EB' }, // gray-50 / gray-600 / gray-200
        };

        const key = Object.keys(colorMap).find(
            k => k.toLowerCase() === title.toLowerCase()
        );

        if (key) {
            const { bg, text, border } = colorMap[key];

            return {
                backgroundColor: bg,
                dotColor: border,
                color: text,
                border: withBorder ? `1px solid ${border}` : 'inherit',
            };
        }

        const index = statuses.findIndex(
            s => s.toLowerCase() === title.toLowerCase()
        );

        if (index !== -1 && index < palettes.length) {
            return {
                backgroundColor: theme.colors[palettes[index]][50],
                dotColor: theme.colors[palettes[index]][400],
                color: theme.colors[palettes[index]][700],
                border: withBorder
                    ? `1px solid ${theme.colors[palettes[index]][200]}`
                    : 'inherit',
            };
        }

        return {
            backgroundColor: theme.colors.neutral[100],
            dotColor: theme.colors.neutral[400],
            color: theme.colors.neutral[600],
            border: withBorder
            ? `1px solid ${theme.colors.neutral[500]}`
            : 'inherit',
        };
    }, [statuses, palettes]);

    const handleSave = useCallback(async (item: string) => {
        const promises: Promise<any>[] = bulkDashboards.map(({dashboard}) => updateDashboardFunc({...dashboard, status: item}));

        if (!!promises.length && status !== item) {
            setSelected(item);
            Promise.all(promises).then(() => {
                const bulkDashboardIds = new Set(bulkDashboards.map(({ dashboard }) => dashboard.id));

                setDashboards(prev => produce(prev, draft => {
                    draft.forEach(d => {
                        if (bulkDashboardIds.has(d.id))
                            d.status = item;
                    });
                }));
            }).finally(clearBulk);
        }
    // eslint-disable-next-line
    }, [bulkDashboards, status]);

    useEffect(() => {
        if (!!bulkDashboards.length)
            setSelected(bulkDashboards[0].dashboard.status);
    }, [bulkDashboards]);

    return (<>
        <Box onClick={(e) => e.stopPropagation()}>
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-start">
                <FormControl className={classes.formContainer} variant="outlined"
                    sx={{ "& > .MuiInputBase-root":{ ...getColors(status, true) } }}>
                    <Select value={status}
                        onChange={(e) => handleSave(e.target.value as string)}
                        renderValue={(status: string) => (
                            <Typography className={classes.badge}> {status} </Typography>
                        )} MenuProps={{ PaperProps: { sx: { borderRadius: 2 }} } }>
                        {statuses.map((item, i) => (
                            <MenuItem className={classes.menuItem}
                                value={item}
                                key={'dashboard-status-menu-170-' + i}>
                                <CircleIcon sx={{ width: 12, height: 12, color: getColors(item)?.dotColor || '#000' }} />
                                <Typography className={classes.badge} ml={1}> {item} </Typography>
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Stack>
        </Box>
    </>);
}

export default CompaniesStatusMenu;